<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');

const activeTab = ref('images');

const tabs = [
  { key: 'images', label: 'Imagens', icon: 'i-lucide-image' },
  { key: 'videos', label: 'Vídeos', icon: 'i-lucide-video' },
  { key: 'audio', label: 'Áudio', icon: 'i-lucide-headphones' },
  { key: 'files', label: 'Arquivos', icon: 'i-lucide-file-text' },
];

const allMessages = computed(() => currentChat.value?.messages || []);

const attachmentMessages = computed(() => {
  return allMessages.value.filter(
    msg => msg.attachments && msg.attachments.length > 0
  );
});

const allAttachments = computed(() => {
  return attachmentMessages.value.flatMap(msg =>
    (msg.attachments || []).map(att => ({
      ...att,
      messageId: msg.id,
      senderName: msg.sender?.name || 'Sistema',
      createdAt: msg.created_at,
    }))
  );
});

const imageAttachments = computed(() =>
  allAttachments.value.filter(a => a.file_type === 'image')
);

const videoAttachments = computed(() =>
  allAttachments.value.filter(a => a.file_type === 'video')
);

const audioAttachments = computed(() =>
  allAttachments.value.filter(a => a.file_type === 'audio')
);

const fileAttachments = computed(() =>
  allAttachments.value.filter(
    a => !['image', 'video', 'audio'].includes(a.file_type)
  )
);

const filteredAttachments = computed(() => {
  const map = {
    images: imageAttachments.value,
    videos: videoAttachments.value,
    audio: audioAttachments.value,
    files: fileAttachments.value,
  };
  return map[activeTab.value] || [];
});

const tabCounts = computed(() => ({
  images: imageAttachments.value.length,
  videos: videoAttachments.value.length,
  audio: audioAttachments.value.length,
  files: fileAttachments.value.length,
}));

const totalCount = computed(() =>
  allAttachments.value.length
);

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const getFileName = (att) => {
  if (att.data_url) {
    const parts = att.data_url.split('/');
    return decodeURIComponent(parts[parts.length - 1]) || 'arquivo';
  }
  return 'arquivo';
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="totalCount === 0" class="flex flex-col items-center justify-center py-6 text-n-slate-10">
      <span class="i-lucide-paperclip size-8 mb-2 opacity-40" />
      <span class="text-xs">Nenhum anexo nesta conversa</span>
    </div>

    <template v-else>
      <!-- Tab bar -->
      <div class="flex gap-1 p-0.5 rounded-lg bg-n-alpha-1">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer"
          :class="[
            activeTab === tab.key
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          @click="activeTab = tab.key"
        >
          <span :class="tab.icon" class="size-3" />
          {{ tab.label }}
          <span
            v-if="tabCounts[tab.key] > 0"
            class="text-[10px] px-1 rounded-full"
            :class="activeTab === tab.key ? 'bg-n-alpha-3' : 'bg-n-alpha-1'"
          >
            {{ tabCounts[tab.key] }}
          </span>
        </button>
      </div>

      <!-- Image grid -->
      <div v-if="activeTab === 'images'" class="grid grid-cols-3 gap-1.5">
        <a
          v-for="att in filteredAttachments"
          :key="att.id"
          :href="att.data_url"
          target="_blank"
          rel="noopener noreferrer"
          class="relative aspect-square rounded-lg overflow-hidden bg-n-alpha-2 group"
        >
          <img
            :src="att.thumb_url || att.data_url"
            :alt="getFileName(att)"
            class="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
            loading="lazy"
          />
          <div class="absolute inset-0 flex items-center justify-center bg-n-solid-1/0 group-hover:bg-n-solid-1/40 transition-colors">
            <span class="i-lucide-maximize-2 size-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </a>
      </div>

      <!-- Video / Audio / File list -->
      <div v-else class="flex flex-col gap-1">
        <a
          v-for="att in filteredAttachments"
          :key="att.id"
          :href="att.data_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2 px-2 py-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors group"
        >
          <span
            class="flex-shrink-0 size-8 rounded-md bg-n-alpha-2 flex items-center justify-center"
          >
            <span
              :class="activeTab === 'videos' ? 'i-lucide-video' : activeTab === 'audio' ? 'i-lucide-headphones' : 'i-lucide-file-text'"
              class="size-4 text-n-slate-10"
            />
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-xs font-medium text-n-slate-12 truncate">
              {{ getFileName(att) }}
            </p>
            <p class="text-[10px] text-n-slate-10">
              {{ formatFileSize(att.file_size) }}
              <span v-if="att.senderName"> · {{ att.senderName }}</span>
            </p>
          </div>
          <span class="i-lucide-download size-3.5 text-n-slate-10 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </a>
      </div>

      <div v-if="filteredAttachments.length === 0" class="flex flex-col items-center py-4 text-n-slate-10">
        <span class="text-[11px]">Nenhum {{ tabs.find(t => t.key === activeTab)?.label?.toLowerCase() }} encontrado</span>
      </div>
    </template>
  </div>
</template>
